import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Container } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Link as ReactLink, useSearchParams } from 'react-router-dom';

import logoBlue from '@/assets/sofar-logo-blue.svg';
import logo from '@/assets/sofar-logo-white.png';
import { ForgotPasswordFormSection } from '@/components/login/ForgotPasswordFormSection';
import { LoginFormSection } from '@/components/login/LoginFormSection';
import { useDisclosure } from '@/hooks/useDisclosure';
import { useGlobalStore } from '@/store';
import { getRedirectPath } from '@/utils/redirectHelpers';

export const Login = () => {
    const isLoggedIn = useGlobalStore((state) => state.isLoggedIn);
    const [searchParams] = useSearchParams();
    const {
        isOpen: isForgotPasswordMode,
        open: switchToForgotPasswordMode,
        close: switchToLoginMode,
    } = useDisclosure();

    if (isLoggedIn) {
        const redirectPath = getRedirectPath(searchParams);

        if (redirectPath) {
            window.location.href = redirectPath;
            return null;
        }

        // TODO: Replace with return <Navigate to="/main" replace /> when Dashboard v2 has a main landing page
        window.location.href = '/'; // Redirect to the old dashboard
        return null;
    }

    return (
        <Grid container component="main" sx={{ height: '100vh' }}>
            <Grid
                size={{ xs: false, sm: 4, md: 7 }}
                sx={{
                    backgroundImage: `url(${logo}), url(https://filedn.com/lTTdn1W2IjNme17D5yWuF74/Resources/waves-2.jpg)`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '70%, cover',
                    backgroundPosition: '25% 10%, 35%',
                }}
            />
            <Grid size={{ xs: 12, sm: 8, md: 5 }} component={Paper} elevation={6} square>
                <Box
                    sx={{
                        my: 8,
                        mx: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Container
                        maxWidth="sm"
                        sx={{
                            display: { xs: 'flex', sm: 'none' },
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 2,
                        }}
                    >
                        <Box sx={{ height: 50 }} component="img" src={logoBlue} alt="sofar ocean logo" />
                    </Container>
                    <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
                        <LockOutlinedIcon />
                    </Avatar>
                    <Typography component="h1" variant="h3">
                        Sign in to Spotter Dashboard
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 4 }} component="form">
                        {isForgotPasswordMode ? <ForgotPasswordFormSection /> : <LoginFormSection />}
                        <Grid container spacing={2} sx={{ width: 1, justifyContent: 'space-between' }}>
                            <Grid>
                                <Link
                                    variant="body2"
                                    onClick={isForgotPasswordMode ? switchToLoginMode : switchToForgotPasswordMode}
                                >
                                    {isForgotPasswordMode ? 'Back to login' : 'Forgot password?'}
                                </Link>
                            </Grid>
                            <Grid>
                                <Typography variant="body2">
                                    Don&apos;t have an account?{' '}
                                    <Link
                                        component={ReactLink}
                                        to={{
                                            pathname: '/signup',
                                            search: searchParams.toString(),
                                        }}
                                    >
                                        Sign up
                                    </Link>
                                </Typography>
                            </Grid>
                        </Grid>
                        <Container sx={{ mt: 5 }}>
                            <Typography variant="body2" color="text.secondary" align="center">
                                {'Copyright © '}
                                <Link color="text.secondary" href="https://sofarocean.com/">
                                    Sofar Ocean
                                </Link>{' '}
                                {new Date().getFullYear()}
                                {'.'}
                            </Typography>
                        </Container>
                    </Grid>
                </Box>
            </Grid>
        </Grid>
    );
};
